import type { DisbursementFilters } from "~/lib/queries";

/**
 * Every filter on the disbursements list, as URL search params.
 *
 * Both text filters are prefix matches on unconstrained varchars. Neither
 * `disbursement_method` nor `destination` has an enum or a CHECK behind it, so
 * there is no dropdown to build without inventing a set that would hide rows.
 */
export interface DisbursementSearch {
  method?: string;
  destination?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

const FILTER_KEYS = [
  "method",
  "destination",
  "start_date",
  "end_date",
] as const satisfies readonly (keyof DisbursementSearch)[];

export function validateDisbursementSearch(raw: Record<string, unknown>): DisbursementSearch {
  return {
    method: str(raw.method),
    destination: str(raw.destination),
    start_date: str(raw.start_date),
    end_date: str(raw.end_date),
    limit: num(raw.limit),
    offset: num(raw.offset),
  };
}

export function hasActiveFilters(search: DisbursementSearch): boolean {
  return FILTER_KEYS.some((key) => search[key] !== undefined);
}

export function toFilters(search: DisbursementSearch): DisbursementFilters {
  return {
    method: search.method,
    destination: search.destination,
    start_date: search.start_date,
    end_date: search.end_date,
    limit: search.limit ?? 25,
    offset: search.offset ?? 0,
  };
}

function str(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function num(value: unknown): number | undefined {
  const parsed = typeof value === "number" ? value : Number(str(value));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}
